import { Alert, Button, Card, Checkbox, Col, Form, FormInstance, Input, InputNumber, message, Radio, Row, Select, Space, Switch, Tag } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { formatInputNumber, parseInputNumber, roundValue } from "../../utils"
import { useConfig } from "../../contexts/ConfigContext"
import { useStore } from "../../contexts/StoreContext"
import { useRegister } from "../../contexts/RegisterContext"
import { useStoreFetch } from "../../hooks/useInventory"
import { useCompany } from "../../contexts/CompanyContext"
import useAuth from "../../hooks/useAuth"
import { useUser } from "../../contexts/UserContext"
import ValidatedEntityInput from "../ValidatedEntityInput"
import { MinusCircleOutlined, CreditCardOutlined } from "@ant-design/icons"
import MovementAttachmentUploader from "../common/MovementAttachmentUploader"

type formProps = {
  form: FormInstance
}

function RegisterMovementExitForm({form}:Readonly<formProps>){
  const {config} = useConfig()
  const {store,updateStore} = useStore()
  const {company} = useCompany()
  const {user} = useUser()
  const {register} = useRegister()
  const idCounter = useRef(-1);
  const getNextId = () => --idCounter.current;
  const [payments, setPayments] = useState([{id:-1,key:"CASH"}]);
  const [enableAdd, setEnableAdd] = useState(false);
  const [payWarning,setPayWarning] = useState(false)
  const lastPendingKeyRef = useRef<string | number | undefined>(undefined);
  const {
    currentPending,
  } = register
  const {tokenData} = useAuth.useToken('authCsrfToken',config.session,config.key)
  const {data} = useStoreFetch(tokenData,{
    all:true,
    company:company.current.uid
  })

  const isElectronicInvoicingEnabled = company.moduleOptions?.some(
    (opt: any) =>
      (opt.key === "FACTUS_ENABLED" || opt.type === "ELECTRONIC_INVOICING") &&
      (opt.show === true || opt.show === 1 || opt.is_active_for_company === true || opt.is_active_for_company === 1 || opt.numeric_value === 1)
  ) || false;

  const approverRequiredOption = company.moduleOptions.find(opt => opt.type === 'MOVEMENT_EXIT_CONFIG' && opt.key === 'APPROVER_REQUIRED');
  const approverRequired = approverRequiredOption ? approverRequiredOption.numeric_value === 1 : false;

  const currentPendingKey = currentPending?.uid || currentPending?.id;
  const total = (currentPending?.total_amount_net) || 0

  const recalculateAmounts = (targetTotalPaid?: number) => {
    const formValues = form.getFieldsValue();
    let totalPaid = 0;
    if (targetTotalPaid !== undefined) {
      totalPaid = targetTotalPaid;
    } else {
      for (const key in formValues) {
        if (key.startsWith("amount_") && formValues[key] !== undefined && formValues[key] !== null) {
          totalPaid += Number(formValues[key]);
        }
      }
    }

    const currentPaymentTerm = form.getFieldValue("payment_term") || "CASH";

    if (currentPaymentTerm === "CREDIT") {
      const pendingBalance = Math.max(0, total - totalPaid);
      form.setFieldsValue({ cash_return_amount: roundValue(pendingBalance) });
      setEnableAdd(totalPaid < total);
    } else {
      const totalReturn = totalPaid - total;
      form.setFieldsValue({ cash_return_amount: roundValue(totalReturn) });
      setEnableAdd(totalPaid < total);
    }
  };

  useEffect(()=>{
    if (currentPendingKey && currentPendingKey !== lastPendingKeyRef.current) {
      lastPendingKeyRef.current = currentPendingKey;
      const isCredit = currentPending?.payment_term === "CREDIT";
      const initialPaid = isCredit ? 0 : ((currentPending?.total_amount_net) || 0);
      const initialReturn = isCredit 
        ? ((currentPending?.total_amount_net) || 0)
        : (currentPending?.cash_return_amount || 0);

      form.setFieldsValue({
        execution_mode: currentPending?.execution_mode || "IMMEDIATE",
        payment_term: currentPending?.payment_term || "CASH",
        credit_days: currentPending?.credit_days || 30,
        total_amount_net: (currentPending?.total_amount_net) || 0,
        cash_return_amount: initialReturn,
        ['amount_-1']: initialPaid,
        ['pay_method_-1']: 'CASH',
        attachments: currentPending?.attachments || [],
        is_electronic_invoice: isElectronicInvoicingEnabled,
        send_email: true,
        customer_email: (currentPending as any)?.contact_email || (currentPending as any)?.email || ""
      });
      setPayments([{ id: -1, key: "CASH" }]);
    }
    if(data?.storehouse){
      updateStore({list: data.storehouse})
    }
  },[data, currentPendingKey, form, isElectronicInvoicingEnabled])

  useEffect(() => {
    if (!approverRequired && user) {
      form.setFieldsValue({
        approved_by_user_id: user.id || 0,
        approved_by_user_name: user.name || "",
        approved_by_user_document: user.document_number || "",
        received_by_user_id: user.id || 0,
        received_by_user_name: user.name || "",
        received_by_user_document: user.document_number || "",
        delivered_by_user_id: user.id || 0,
        delivered_by_user_name: user.name || "",
        delivered_by_user_document: user.document_number || ""
      });
    }
  }, [approverRequired, user, form]);
  
  const handleEntityChange = (field: string, data: { id: number; name: string; document: string; email?: string; electronic_invoice_email?: string; entity?: any }) => {
    form.setFieldsValue({
      [`${field}_id`]: data.id,
      [`${field}_name`]: data.name,
      [`${field}_document`]: data.document
    });
    // In case of contact_id which is a bit different
    if (field === 'contact') {
      const contactEmail = data.email || data.electronic_invoice_email || (data.entity && (data.entity.email || data.entity.electronic_invoice_email)) || "";
      form.setFieldsValue({
        contact_id: data.id,
        contact_name: data.name,
        contact_document: data.document,
        customer_email: contactEmail || "",
        send_email: Boolean(contactEmail)
      });
    }
  };

  const removePay = (id:number) => {
    if(id > 0){
      message.error("No se puede eliminar un medio de pago que ya se encuentra registrado")
      return
    }
    const filterResult = payments.filter(item => item.id !== id)
    const result = filterResult.length ? filterResult : [{ id: -1,key:"CASH"}]
    if(result[0].id === -1 && result.length === 1 && payments.length === 1){
      form.setFieldsValue({[`pay_method_${result[0].id}`]: "CASH"})
    }
    setPayments(result)
    const formValues = form.getFieldsValue()
    let totalPaid = 0
    for(const key in formValues){
      if(key.startsWith("amount_") && formValues[key] !== undefined && formValues[key] !== null && !key.includes(id.toString())){
        totalPaid += Number(formValues[key])
      }
    } 
    recalculateAmounts(totalPaid);
  }

  const changeReceived = (_value:number | null) => {
    recalculateAmounts();
  }

  const scanForEmptyFields = (data:any,setData:any,setShoWarning:any,key:string)=>{
    const values = form.getFieldsValue()
    let emptyValues = false
    for(const str in values){
      emptyValues = str.startsWith(key) && values[str] === undefined
      if(emptyValues) {
        break
      }
    }
    if(emptyValues){
      setShoWarning(true)
    }else{
      const id = getNextId()
      setData([...data, { id: id,key:"CASH" }]);
      form.setFieldsValue({[`pay_method_${id}`]: "CASH"})
      setShoWarning(false)
    }
  }

  const onChangePayMethod = (value:any,id:number) => {
    const payArray = [...payments]
    const index = payArray.findIndex(item => item.id === id)
    if(index === -1){
      return
    }
    payArray[index].key = value
    setPayments(payArray)
  }

  const movementType = Form.useWatch("document_type",form)
  const isElectronicInvoiceWatch = Form.useWatch("is_electronic_invoice",form)
  const paymentTermWatch = Form.useWatch("payment_term",form) || "CASH"
  
  return(
  <>
    <Row gutter={[10, 10]} align="middle">
      <Form.Item name="approved_by_user_id" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="approved_by_user_name" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="approved_by_user_document" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="delivered_by_user_id" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="delivered_by_user_name" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="delivered_by_user_document" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="received_by_user_id" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="received_by_user_name" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="received_by_user_document" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="contact_id" hidden><Input type="hidden" /></Form.Item>
      <Form.Item name="contact_name" hidden><Input type="hidden" /></Form.Item>
      {movementType !== "WASTE" && <Col xs={24} sm={{span:12}}>
        <ValidatedEntityInput
          type="CUSTOMER"
          label={movementType === "QUOTATION" ? "Ofrecido a" : movementType === "SALE_INVOICE" ? "Cliente" : "Entregado a"}
          value={currentPending?.contact_document}
          initialName={currentPending?.contact_name}
          initialId={currentPending?.contact_id}
          rules={[{ required: true, message: movementType === "QUOTATION" ? 'Indique a quién va ofrecida la cotización' : movementType === "SALE_INVOICE" ? 'Cliente requerido' : 'Destinatario requerido' }]}
          name="contact_document"
          onChange={(data) => handleEntityChange('contact', data)}
        />
      </Col>}
      {approverRequired && <>
        <Col xs={24} sm={{span:12}}>
          <ValidatedEntityInput
            type="USER"
            label="Entregado por"
            value={currentPending?.delivered_by_user_document}
            initialName={currentPending?.delivered_by_user_name}
            initialId={currentPending?.delivered_by_user_id}
            rules={[{ required: true, message: 'Entregador requerido' }]}
            name="delivered_by_user_document"
            onChange={(data) => handleEntityChange('delivered_by_user', data)}
          />
        </Col>

        <Col xs={24} sm={{span:12}}>
          <ValidatedEntityInput
            type="USER"
            label="Aprobador"
            value={currentPending?.approved_by_user_document}
            initialName={currentPending?.approved_by_user_name}
            initialId={currentPending?.approved_by_user_id}
            rules={[{ required: true, message: 'Aprobador requerido' }]}
            name="approved_by_user_document"
            onChange={(data) => handleEntityChange('approved_by_user', data)}
          />
        </Col> 
      </>}
      <Col xs={24} sm={{span:12}}>
        <Form.Item
          label="Detalle de orden"
          name={`notes`}
          rules={[{ required: true, message: 'Se requiere una nota'}]}
        >
          <Input placeholder="Comentarios adicionales" style={{ width: '100%' }}></Input>
        </Form.Item>
      </Col>
      <Col xs={24} sm={{span:12}}>
        <Form.Item
          label={movementType === "QUOTATION" ? "Total cotización" : "Total factura"}
          name={`total_amount_net`}
          rules={[{ required: true, message: ''}]}
        >
          <InputNumber 
            disabled prefix="$" min={1}
            formatter={formatInputNumber}
            parser={parseInputNumber}
            placeholder={movementType === "QUOTATION" ? "Valor cotizado" : "Valor facturado"} 
            style={{ width: '100%' }}>
          </InputNumber>
        </Form.Item>
      </Col>

      <Col xs={24} sm={{span:12}}>
        <Form.Item
          label={
            movementType === "QUOTATION" ? (
              <Space size={4}>
                <span>Almacén previsto</span>
                <Tag color="geekblue" style={{ fontSize: 10, marginRight: 0 }}>Referencia</Tag>
              </Space>
            ) : "Almacén origen"
          }
          tooltip={movementType === "QUOTATION" ? "Almacén sugerido o proyectado para despacho futuro en caso de aprobarse la cotización." : undefined}
          name={`storehouse_from_id`}
          rules={[{ required: true, message: 'Seleccione un almacén'}]}
        >
          <Select style={{width:"100%"}}>
            {store.list.map(el => <Select.Option key={el.id} value={el.id}>{el.name}</Select.Option>)}
          </Select>
        </Form.Item>
      </Col>
    </Row>
    {movementType === "SALE_INVOICE" && <>
      <Card 
        size="small" 
        title={<Space><CreditCardOutlined /><span>Condición y Flujo de la Venta</span></Space>}
        style={{ marginBottom: 16, border: '1px solid #d9d9d9', borderRadius: 8 }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12}>
            <Form.Item
              name="execution_mode"
              label="Flujo de Entrega / Despacho"
              initialValue="IMMEDIATE"
              tooltip="Inmediato: La mercancía sale de bodega ya mismo y descuenta el inventario. Diferido: Se factura hoy pero queda pendiente de entrega; se despacha después por remisión."
              style={{ marginBottom: 0 }}
            >
              <Radio.Group 
                buttonStyle="solid"
                onChange={(e) => {
                  form.setFieldsValue({ execution_mode: e.target.value })
                }}
              >
                <Radio.Button value="IMMEDIATE">⚡ Salida Inmediata</Radio.Button>
                <Radio.Button value="DEFERRED">📋 Despacho Diferido</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="payment_term"
              label="Condición de Pago"
              rules={[{ required: true, message: 'Seleccione la condición de pago' }]}
              style={{ marginBottom: 0 }}
            >
              <Radio.Group 
                buttonStyle="solid"
                onChange={(e) => {
                  form.setFieldsValue({ payment_term: e.target.value })
                  recalculateAmounts()
                }}
              >
                <Radio.Button value="CASH">Contado</Radio.Button>
                <Radio.Button value="CREDIT">Crédito</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
          {paymentTermWatch === "CREDIT" && (
            <Col xs={24} md={12}>
              <Form.Item
                name="credit_days"
                label="Días de crédito"
                rules={[{ required: true, message: 'Seleccione el plazo de crédito' }]}
                style={{ marginBottom: 0 }}
              >
                <Select placeholder="Seleccione plazo">
                  <Select.Option value={8}>8 días</Select.Option>
                  <Select.Option value={15}>15 días</Select.Option>
                  <Select.Option value={30}>30 días (1 mes)</Select.Option>
                  <Select.Option value={45}>45 días</Select.Option>
                  <Select.Option value={60}>60 días (2 meses)</Select.Option>
                  <Select.Option value={90}>90 días (3 meses)</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Card>

      <h5 style={{marginLeft:"8.3333%",marginBottom:"0.5em",fontWeight:500,fontSize:"1rem"}}>
        {paymentTermWatch === "CREDIT" ? "Abono inicial (Opcional)" : "Datos de pago"}
      </h5>
      {payments.map((pay,index) => (
        <Row key={pay.id} gutter={[10,10]} align="middle">
          <Col xs={24} sm={{span:12}}>
            <Form.Item
              label= {index === 0 ? (paymentTermWatch === "CREDIT" ? "Medio de pago inicial" : "Medio de pago") : ""}
              name={`pay_method_${pay.id}`}
              rules={[{ required: true, message: 'Selecciona el medio pago' }]}
            >
            <Select placeholder="Selecciona el medio pago" onChange={(value) => onChangePayMethod(value,pay.id)}>
              {register.paymentOptions.map(el => 
                <Select.Option key={el.id} value={el.key}>{el.string_value}</Select.Option>
              )}
            </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={{span:12}}>
            <Form.Item
              label={index === 0 ? (paymentTermWatch === "CREDIT" ? "Abono inicial" : "Total pagado") : ""}
              name={`amount_${pay.id}`}
              rules={[{ 
                required: paymentTermWatch !== "CREDIT", 
                message: "Ingrese el valor" 
              }]}>
              <InputNumber 
                prefix="$" 
                min={paymentTermWatch === "CREDIT" ? 0 : 1} 
                placeholder={paymentTermWatch === "CREDIT" ? "0 (Crédito total)" : "Monto"}
                style={{width:"100%"}} 
                formatter={formatInputNumber}
                parser={parseInputNumber}
                onChange={(e) => changeReceived(e)}>
              </InputNumber>
            </Form.Item>
          </Col>
          <Col span={2} style={{marginTop: index === 0 ? "16px":"-12px"}}>
          {
            payments.length > 1 ? (
              <MinusCircleOutlined 
                onClick={() => removePay(pay.id)}
              />
            ) : null
          }
          </Col>
          {form.getFieldValue(`pay_method_${pay.id}`) !== "CASH" && <Col xs={24} sm={{span:12}}>
            <Form.Item
              label= "Referencia"
              name={`reference_${pay.id}`}
              rules={[{ 
                required: (Number(form.getFieldValue(`amount_${pay.id}`)) || 0) > 0, 
                message: 'Ingrese la referencia' 
              }]}
            >
              <Input placeholder="Referencia" style={{ width: '100%', marginBottom: "1em" }}></Input>
            </Form.Item>
          </Col>}
        </Row>
      ))}
      <Row gutter={[20,10]}>
        <Col xs={24} sm={{span:12}}>
          {payWarning && <Alert
            message="Complete los campos antes de añadir otro pago"
            banner
            closable
            onClose={()=>setPayWarning(false)}
          />}
        </Col>
      </Row>
      {enableAdd && <Row gutter={[20,10]} style={{marginBottom:5}}>
        <Col xs={8} sm={{span:5}}>
          <Button type="dashed" style={{marginTop:"5px"}} onClick={() => scanForEmptyFields(payments,setPayments,setPayWarning,'amount_')}>Agregar pago</Button>
        </Col>
      </Row>}
      <Row gutter={[10,10]}> 
        <Col sm={12} xs={{span:12}}>
          <Form.Item
            label={paymentTermWatch === "CREDIT" ? "Saldo a Crédito (Pendiente)" : "Total por cubrir/devolver"}
            name="cash_return_amount"
            rules={[
              {
                validator: async (_, val) => {
                  if (paymentTermWatch === "CREDIT") {
                    return Promise.resolve()
                  }
                  if (val === undefined || val === null || val < 0) {
                    return Promise.reject(new Error("Valide el total de la venta"))
                  }
                  return Promise.resolve()
                }
              }
            ]}>
            <InputNumber 
              prefix="$" min={0} 
              style={{
                width:"100%",
                ...(paymentTermWatch === "CREDIT" && (form.getFieldValue("cash_return_amount") || 0) > 0 ? {
                  borderColor: '#ff4d4f',
                  color: '#cf1322',
                  fontWeight: 'bold'
                } : {})
              }} 
              formatter={formatInputNumber}
              parser={parseInputNumber}
              disabled>
            </InputNumber>
          </Form.Item>
        </Col>
      </Row>
      {isElectronicInvoicingEnabled && (
        <div style={{
          backgroundColor: '#f6ffed',
          border: '1px solid #b7eb8f',
          borderRadius: 8,
          padding: '12px 16px',
          marginTop: 12,
          marginBottom: 16
        }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: isElectronicInvoiceWatch !== false ? 12 : 0 }}>
            <Space>
              <Tag color="green" style={{ fontSize: 13, padding: '2px 8px' }}>
                DIAN
              </Tag>
              <strong style={{ color: '#237804' }}>Factura Electrónica de Venta</strong>
            </Space>
            <Form.Item name="is_electronic_invoice" valuePropName="checked" noStyle>
              <Switch checkedChildren="Sí" unCheckedChildren="No" />
            </Form.Item>
          </Row>

          {isElectronicInvoiceWatch !== false && (
            <Row gutter={[10, 10]}>
              <Col span={24}>
                <Form.Item
                  name="customer_email"
                  label="Correo electrónico del cliente"
                  rules={[
                    { type: 'email', message: 'Ingrese un correo electrónico válido' }
                  ]}
                  style={{ marginBottom: 8 }}
                >
                  <Input placeholder="correo@ejemplo.com" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="send_email" valuePropName="checked" noStyle>
                  <Checkbox defaultChecked>
                    Enviar factura electrónica automáticamente al correo del cliente
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
          )}
        </div>
      )}
    </>}
    <Row gutter={[10, 10]} style={{ marginTop: 12 }}>
      <Col span={24}>
        <Form.Item name="attachments" label={null} style={{ marginBottom: 0 }}>
          <MovementAttachmentUploader
            movementId={currentPending?.id}
            movementUid={currentPending?.uid}
            defaultDocType={movementType}
          />
        </Form.Item>
      </Col>
    </Row>
  </>)
}

export default RegisterMovementExitForm